import { Injectable, inject } from '@angular/core';
import { expand, map, Observable, reduce, EMPTY } from 'rxjs';
import { SoffApiCallResponse, SoffApiResponseDto, SoffApiService } from '@soff/shared-ui';
import {
  DisplayStatus,
  EscalationLevel,
  EscalationPattern,
  TscEmployeeDto,
  TscRegionDto,
  TscSalesOrganizationDto,
  TscTicketQueryDto,
  TscWorkshopDto,
} from '@soff/tsc-ui/core';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class TscAdminService {
  private api = inject(SoffApiService);

  private readonly employeesUrl = environment.apiBaseUrl + '/v1/tsc/employees/';
  private readonly workshopsUrl = environment.apiBaseUrl + '/v1/tsc/workshops/';
  private readonly regionUrl = environment.apiBaseUrl + '/v1/tsc/admin/regions/';
  private readonly escalationUrl = environment.apiBaseUrl + '/v1/tsc/admin/escalation/';

  loadEmployees(query?: TscTicketQueryDto): Observable<SoffApiResponseDto> {
    const params = ['relatedAggregateTypes=Workshop'];
    if (typeof query?.limit === 'number') {
      params.push(`limit=${query.limit}`);
    }
    if (typeof query?.offset === 'number') {
      params.push(`offset=${query.offset}`);
    }
    if (query?.order) {
      params.push(`orderBy=${query.order}`);
    }
    if (query?.filter) {
      params.push(`filter=${encodeURIComponent(query.filter)}`);
    }
    if (query?.query) {
      params.push(query.query);
    }
    return this.api.get(`${this.employeesUrl}?${params.join('&')}`).pipe(map((resp: SoffApiCallResponse) => resp?.body));
  }

  getEmployee(userId: string): Observable<SoffApiResponseDto> {
    return this.api.get(`${this.employeesUrl}${userId}?relatedAggregateTypes=Workshop`).pipe(map((resp: SoffApiCallResponse) => resp?.body));
  }

  updateEmployee(employee: Partial<TscEmployeeDto> & Pick<TscEmployeeDto, 'aggregateId'>): Observable<SoffApiResponseDto> {
    return this.api.patch(`${this.employeesUrl}${employee.aggregateId}`, employee).pipe(map((resp: SoffApiCallResponse) => resp?.body));
  }

  loadWorkshops(query?: TscTicketQueryDto): Observable<SoffApiResponseDto> {
    const filter = [];
    if (!query?.filter?.includes('level=')) {
      filter.push(`level=in=${Object.values(EscalationLevel).join(',')}`);
    }
    if (query?.filter) {
      filter.push(query.filter);
    }
    const params = [`filter=${encodeURIComponent(filter.join(';'))}`];
    if (typeof query?.limit === 'number') {
      params.push(`limit=${query.limit}`);
    }
    if (typeof query?.offset === 'number') {
      params.push(`offset=${query.offset}`);
    }
    if (query?.order) {
      params.push(`orderBy=${query.order}`);
    }
    if (query?.query) {
      params.push(query.query);
    }
    return this.api.get(`${this.workshopsUrl}workshop/?${params.join('&')}`).pipe(map((resp: SoffApiCallResponse) => resp?.body));
  }

  updateWorkshop(workshop: Partial<TscWorkshopDto> & Pick<TscWorkshopDto, 'aggregateId'>): Observable<SoffApiResponseDto> {
    return this.api.patch(`${this.workshopsUrl}workshop/${workshop.aggregateId}`, workshop).pipe(map((resp: SoffApiCallResponse) => resp?.body));
  }

  getWorkshop(id: string): Observable<SoffApiResponseDto> {
    return this.api.get(`${this.workshopsUrl}workshop/${id}`).pipe(map((resp: SoffApiCallResponse) => resp?.body));
  }

  loadDisplayStatus(query?: TscTicketQueryDto): Observable<SoffApiResponseDto> {
    const params: string[] = [];
    if (typeof query?.limit === 'number') {
      params.push(`limit=${query.limit}`);
    }
    if (typeof query?.offset === 'number') {
      params.push(`offset=${query.offset}`);
    }
    if (query?.order) {
      params.push(`orderBy=${query.order}`);
    }
    if (query?.filter) {
      params.push(`filter=${encodeURIComponent(query.filter)}`);
    }
    if (query?.query) {
      params.push(query.query);
    }
    return this.api
      .get(`${this.escalationUrl}displayStatus${params.length ? `?${params.join('&')}` : ''}`)
      .pipe(map((resp: SoffApiCallResponse) => resp?.body));
  }

  createDisplayStatus(displayStatus: DisplayStatus): Observable<SoffApiResponseDto> {
    return this.api.post(`${this.escalationUrl}displayStatus`, displayStatus).pipe(map((resp: SoffApiCallResponse) => resp?.body));
  }

  updateDisplayStatus(displayStatus: Partial<DisplayStatus> & Pick<DisplayStatus, 'aggregateId'>): Observable<SoffApiResponseDto> {
    return this.api
      .patch(`${this.escalationUrl}displayStatus/${displayStatus.aggregateId}`, displayStatus)
      .pipe(map((resp: SoffApiCallResponse) => resp?.body));
  }

  // TODO review usages: Should use store
  loadRegions(query?: TscTicketQueryDto): Observable<SoffApiResponseDto> {
    const params: string[] = [];
    if (typeof query?.limit === 'number') {
      params.push(`limit=${query.limit}`);
    }
    if (typeof query?.offset === 'number') {
      params.push(`offset=${query.offset}`);
    }
    if (query?.order) {
      params.push(`orderBy=${query.order}`);
    }
    if (query?.filter) {
      params.push(`filter=${encodeURIComponent(query.filter)}`);
    }
    if (query?.query) {
      params.push(query.query);
    }
    return this.api
      .get(`${this.regionUrl}region/${params.length ? `?${params.join('&')}` : ''}`)
      .pipe(map((resp: SoffApiCallResponse) => resp?.body));
  }

  createRegion(region: TscRegionDto): Observable<SoffApiResponseDto> {
    return this.api.post(`${this.regionUrl}region`, region).pipe(map((resp: SoffApiCallResponse) => resp?.body));
  }

  updateRegion(region: Partial<TscRegionDto> & Pick<TscRegionDto, 'aggregateId'>): Observable<SoffApiResponseDto> {
    return this.api.patch(`${this.regionUrl}region/${region.aggregateId}`, region).pipe(map((resp: SoffApiCallResponse) => resp?.body));
  }

  loadVkorgs(query?: TscTicketQueryDto): Observable<SoffApiResponseDto> {
    const params: string[] = [];
    if (typeof query?.limit === 'number') {
      params.push(`limit=${query.limit}`);
    }
    if (typeof query?.offset === 'number') {
      params.push(`offset=${query.offset}`);
    }
    if (query?.order) {
      params.push(`orderBy=${query.order}`);
    }
    if (query?.filter) {
      params.push(`filter=${encodeURIComponent(query.filter)}`);
    }
    if (query?.query) {
      params.push(query.query);
    }
    return this.api
      .get(`${this.regionUrl}salesOrganization/${params.length ? `?${params.join('&')}` : ''}`)
      .pipe(map((resp: SoffApiCallResponse) => resp?.body));
  }

  createVkorg(vkorg: TscSalesOrganizationDto): Observable<SoffApiResponseDto> {
    return this.api.post(`${this.regionUrl}salesOrganization`, vkorg).pipe(map((resp: SoffApiCallResponse) => resp?.body));
  }

  updateVkorg(vkorg: Partial<TscSalesOrganizationDto> & Pick<TscSalesOrganizationDto, 'aggregateId'>): Observable<SoffApiResponseDto> {
    return this.api.patch(`${this.regionUrl}salesOrganization/${vkorg.aggregateId}`, vkorg).pipe(map((resp: SoffApiCallResponse) => resp?.body));
  }

  // TODO review usages: Should use store
  loadEscalationPatterns(query?: TscTicketQueryDto): Observable<SoffApiResponseDto> {
    const params: string[] = [];
    if (typeof query?.limit === 'number') {
      params.push(`limit=${query.limit}`);
    }
    if (typeof query?.offset === 'number') {
      params.push(`offset=${query.offset}`);
    }
    if (query?.order) {
      params.push(`orderBy=${query.order}`);
    }
    if (query?.filter) {
      params.push(`filter=${encodeURIComponent(query.filter)}`);
    }
    if (query?.query) {
      params.push(query.query);
    }
    return this.api
      .get(`${this.escalationUrl}escalationPattern/${params.length ? `?${params.join('&')}` : ''}`)
      .pipe(map((resp: SoffApiCallResponse) => resp?.body));
  }

  createEscalationPattern(pattern: EscalationPattern): Observable<SoffApiResponseDto> {
    return this.api.post(`${this.escalationUrl}escalationPattern`, pattern).pipe(map((resp: SoffApiCallResponse) => resp?.body));
  }

  updateEscalationPattern(pattern: Partial<EscalationPattern> & Pick<EscalationPattern, 'aggregateId'>): Observable<SoffApiResponseDto> {
    return this.api
      .patch(`${this.escalationUrl}escalationPattern/${pattern.aggregateId}`, pattern)
      .pipe(map((resp: SoffApiCallResponse) => resp?.body));
  }

  // --- Export: fetch all pages ---

  fetchAllEmployees(query?: TscTicketQueryDto): Observable<TscEmployeeDto[]> {
    return this.fetchAllPages(q => this.loadEmployees(q), query);
  }

  fetchAllWorkshops(query?: TscTicketQueryDto): Observable<TscWorkshopDto[]> {
    return this.fetchAllPages(q => this.loadWorkshops(q), query);
  }

  fetchAllRegions(query?: TscTicketQueryDto): Observable<TscRegionDto[]> {
    return this.fetchAllPages(q => this.loadRegions(q), query);
  }

  fetchAllVkorgs(query?: TscTicketQueryDto): Observable<TscSalesOrganizationDto[]> {
    return this.fetchAllPages(q => this.loadVkorgs(q), query);
  }

  fetchAllEscalationPatterns(query?: TscTicketQueryDto): Observable<EscalationPattern[]> {
    return this.fetchAllPages(q => this.loadEscalationPatterns(q), query);
  }

  fetchAllDisplayStatuses(query?: TscTicketQueryDto): Observable<DisplayStatus[]> {
    return this.fetchAllPages(q => this.loadDisplayStatus(q), query);
  }

  private fetchAllPages<T>(loadFn: (query: TscTicketQueryDto) => Observable<SoffApiResponseDto>, baseQuery?: TscTicketQueryDto): Observable<T[]> {
    const pageSize = 200;
    let currentOffset = 0;

    const fetchPage = (): Observable<{ response: SoffApiResponseDto; offset: number }> =>
      loadFn({ ...baseQuery, limit: pageSize, offset: currentOffset }).pipe(map(response => ({ response, offset: currentOffset })));

    return fetchPage().pipe(
      expand(({ response }) => {
        const total = response?.meta?.pagination?.total;
        if (total == null) return EMPTY;
        currentOffset += pageSize;
        if (currentOffset >= total) return EMPTY;
        return fetchPage();
      }),
      reduce((acc: T[], { response }) => [...acc, ...(response?.data || [])], [])
    );
  }
}
